import React from 'react';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import withHandlers from 'recompose/withHandlers';

import AutoSaveImagePicker from 'components/forms/AutoSaveFormFields/AutoSaveImagePicker';
import {breakImagesPageName} from "pages/EditBreak/utils";
import {createAllResourceItemsSelector, createResourceTypeSelector} from 'resources/selectors';
import * as ImageActions from 'pages/Images/actions';
import {extractResourceIdsWithPath, getBreakResource, isNoU} from "../utils/DBUtils";
import * as ResourceApi from 'api/resources';
import {startEditImageAction, uploadOriginalAndCreateImageAction} from "../pages/Images/actions";
import connectBreakField from "./BreakField";
import {isNativeUploadBetaEnabled} from "../utils/nativeUploadBeta";

const getAllImages = createAllResourceItemsSelector(breakImagesPageName);
const getImageItems = createResourceTypeSelector(ResourceApi.Types.Image);

const mapState = (state, ownProps) => {
  let masterBreak = ownProps.masterBreak;
  let platformBreak = ownProps.platformBreak;
  let currentResource = getBreakResource(masterBreak, platformBreak, ownProps.resourcePath);

  let id = currentResource.resourceId.id;
  let field = ownProps.fieldPath[ownProps.fieldPath.length - 1];
  let isCreating = false;
  if (!isNoU(state.images.creatingImageFor)) {
    isCreating = (id === state.images.creatingImageFor.id) && (field === state.images.creatingImageFor.field);
  }

  const loadedImages = getImageItems(state.resources);
  const possibleImages = getAllImages(state.resources);

  // Case of creating a new image, the image will not be on the parent object so idAssetValue with be undefined
  // Then we check possible images for the image data by id and use that
  let idAssetValue = ownProps.idAssetValue;
  let idAssetValueId = ownProps.defaultValue;
  let item = loadedImages.find((item) => item.id === idAssetValueId);
  if (!isNoU(item)) {
    idAssetValue = item;
  }

  return {
    loadedImages,
    possibleImages,
    idAssetValue,
    isCreating
  };
};

const mapDispatch = (dispatch, ownProps) => {
  const createImage = (imageId) => {
    let masterResource = ownProps.masterBreak;
    let rootResource = ownProps.platformBreak;
    let modifiedResource = ownProps.formProxy.resource[ownProps.platform];
    let resourcePath = ownProps.resourcePath;
    let parentIds = extractResourceIdsWithPath(masterResource, rootResource, resourcePath);
    let field = ownProps.fieldPath[ownProps.fieldPath.length - 1];

    dispatch(ImageActions.createImageAction(
      imageId,
      ownProps.imageType,
      rootResource,
      modifiedResource.resourceId,
      resourcePath,
      ownProps.fieldPath,
      field,
      parentIds
    ));
  };

  return {
    onValueChanged: (field, value, errors, possibleImages) => {
      if (value === ownProps.value) {
        // no change just return
        return;
      }
      if (value === null) {
        ownProps.onValueChanged(value, ownProps.platform);
      }

      let selectedImage = possibleImages.find((item) => item.id === value);
      if (!selectedImage) {
        // Raise warning, but save any way in case of id input
        // Any originals should have been loaded from the image uploader accordion to trigger create image
        // Direct id input will hopefully be replaced with something better
        console.warn(`Selected image for ${ownProps.name} not loaded.`);
        ownProps.onValueChanged(value, ownProps.platform);
        return;
      }

      if (selectedImage.imageType === "original") {
        createImage(value, ownProps.platform);
      } else {
        ownProps.onValueChanged(value, ownProps.platform);
      }
    },
    onBrowse: () => {
      let masterResource = ownProps.formProxy.baseResource["MASTER"];
      let rootResource = ownProps.formProxy.baseResource[ownProps.platform];
      let modifiedResource = ownProps.formProxy.resource[ownProps.platform];
      let resourcePath = ownProps.formProxy.resourcePath;
      let parentIds = extractResourceIdsWithPath(masterResource, rootResource, resourcePath);
      let field = ownProps.formProxy.fieldPath[ownProps.formProxy.fieldPath.length - 1];

      // console.log('root', rootResource);

      if (isNativeUploadBetaEnabled()) {
        // Open the native picker/crop modal (mounted once at the page level, see
        // ImageUploadModalContainer) instead of kicking off the Filestack flow. It
        // reports back via nativeUploadImageAction once the user confirms.
        dispatch(ImageActions.openNativeUploadModalAction(
          ownProps.imageType,
          rootResource,
          modifiedResource.resourceId,
          resourcePath,
          ownProps.formProxy.fieldPath,
          field,
          parentIds
        ));
      } else {
        dispatch(uploadOriginalAndCreateImageAction(
          ownProps.imageType,
          rootResource,
          modifiedResource.resourceId,
          resourcePath,
          ownProps.formProxy.fieldPath,
          field,
          parentIds
        ));
      }
    },
    editImage: (image) => {
      if (!isNativeUploadBetaEnabled()) {
        createImage(image.originalImageId);
        return;
      }

      // Open the native re-crop modal (mounted once at the page level, see
      // ImageRecropModalContainer) instead of dispatching into the Filestack-only
      // createImageEpic.
      let masterResource = ownProps.formProxy.baseResource["MASTER"];
      let rootResource = ownProps.formProxy.baseResource[ownProps.platform];
      let modifiedResource = ownProps.formProxy.resource[ownProps.platform];
      let resourcePath = ownProps.formProxy.resourcePath;
      let parentIds = extractResourceIdsWithPath(masterResource, rootResource, resourcePath);
      let field = ownProps.formProxy.fieldPath[ownProps.formProxy.fieldPath.length - 1];

      dispatch(ImageActions.openNativeRecropModalAction(
        ownProps.imageType,
        rootResource,
        modifiedResource.resourceId,
        resourcePath,
        ownProps.formProxy.fieldPath,
        field,
        parentIds,
        image.originalImageId
      ));
    },
    // deleteImage: (imageId) => dispatch(deleteImageAction(imageId)),
    editImageInfo: (image) => dispatch(startEditImageAction(image.id)),
    onImageClicked: (image) => console.log("image clicked", image)
  };
};

const ComposedAutoSaveImagePickerBreakField = compose(
  connect(mapState, mapDispatch),
  withHandlers({
    onValueChanged: (props) => (value, field) => {
      props.onValueChanged(field, value, null, props.possibleImages);
    },
    onBrowse: (props) => () => props.onBrowse(),
    editImage: (props) => () => props.editImage(props.idAssetValue),
    editImageInfo: (props) => () => props.editImageInfo(props.idAssetValue),
    onImageClicked: (props) => (image) => props.onImageClicked(image),
    getPossibleImages: (props) => () => props.possibleImages,
    hasOriginalImage: (props) => () => props.idAssetValue ? !isNoU(props.idAssetValue.originalImageId) : false,
    getImageOptions: (props) => () => {
      let options = props.possibleImages.map((image) => ({
        value: image.id,
        text: image.name
      }));

      // Push current image so it shows in dropdown as selected
      if (props.idAssetValue) {
        options.push({
          value: props.idAssetValue.id,
          text: props.idAssetValue.name
        });
      }

      // If there is no value put null option to beginning of options
      if (isNoU(props.defaultValue) || props.defaultValue === "") {
        options.unshift({
          value: null,
          text: "-- Select Image --"
        });
      }

      return options;
    }
  })
);

const AutoSaveImagePickerBreakField = connectBreakField(
  AutoSaveImagePicker,
  {
    preOmit: ComposedAutoSaveImagePickerBreakField
  },
  "loadedImages",
  "possibleImages",
  // "idAssetValue"
);

export default AutoSaveImagePickerBreakField;



// WEBPACK FOOTER //
// ./public/hoc/BreakImagePicker.jsx